import { withNamespaces } from "react-i18next";
import { Text, View } from "@react-pdf/renderer";
import styles from "../../Styles";

const MetricTable = ({ t, rows }) => (
  <View style={styles.table} wrap={false}>
    <View style={styles.tableRow} wrap={false} minPresenceAhead={24}>
      <View style={styles.tableColHeader}>
        <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
          {t("Metric")}
        </Text>
      </View>
      <View style={styles.tableColHeader}>
        <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
          {t("Value")}
        </Text>
      </View>
      <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
        <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
          {t("Details")}
        </Text>
      </View>
    </View>

    {rows.map((r, i) => {
      const rowStyle = {
        ...styles.tableRow,
        ...(i === rows.length - 1 ? styles.tableLastRow : {}),
      };

      return (
        <View style={rowStyle} key={i} wrap={false}>
          <View style={styles.tableCol}>
            <Text style={styles.tableCell}>{t(r.metric)}</Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={styles.tableCell}>{r.value ?? "-"}</Text>
          </View>
          <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
            <Text style={styles.tableCell}>{t(r.details ?? "-")}</Text>
          </View>
        </View>
      );
    })}
  </View>
);

const FinancialSummaryReport = ({
  t,
  money,
  fsAttrs,
  percent,
  financial_summary,
  currencyLocal,
}) => {
  const incomeStatementRows = [
    {
      metric: "Revenue",
      value: money(fsAttrs?.revenue?.value, fsAttrs?.revenue?.unit),
      details: "Annual turnover",
    },
    {
      metric: "Earnings",
      value: money(fsAttrs?.earnings?.value, fsAttrs?.earnings?.unit),
      details: "Net profit for the period",
    },
    {
      metric: "Tax",
      value: money(fsAttrs?.tax?.value, fsAttrs?.tax?.unit),
      details: "Tax expense",
    },
    {
      metric: "Revenue CAGR",
      value: percent(fsAttrs?.revenueCAGR?.value),
      details: "Compound annual growth rate",
    },
    {
      metric: "Earnings CAGR",
      value: percent(fsAttrs?.earningsCAGR?.value),
      details: "Compound annual growth rate",
    },
  ];

  const efficiencyRows = [
    {
      metric: "Return on Sales",
      value: percent(
        fsAttrs?.returnOnSales?.value ?? financial_summary?.returnOnSales
      ),
      details: "Margin on revenue",
    },
    {
      metric: "Return on Equity",
      value: percent(
        fsAttrs?.returnOnEquity?.value ?? financial_summary?.returnOnEquity
      ),
      details: "Profitability relative to equity",
    },
    {
      metric: "Revenue per Employee",
      value:
        fsAttrs?.revenuePerEmployee?.value !== undefined
          ? `~${money(
              fsAttrs.revenuePerEmployee.value,
              fsAttrs.revenuePerEmployee.unit
            )}`
          : financial_summary?.revenuePerEmployee
          ? `~${money(financial_summary.revenuePerEmployee, currencyLocal)}`
          : "-",
      details: "Estimated productivity per staff member",
    },
    {
      metric: "Tax Ratio",
      value: percent(fsAttrs?.taxRatio?.value ?? financial_summary?.taxRatio),
      details: "Effective tax rate",
    },
  ];

  const balanceSheetRows = [
    {
      metric: "Balance Sheet Total",
      value: money(fsAttrs?.balanceTotal?.value, fsAttrs?.balanceTotal?.unit),
      details: "Total assets/liabilities",
    },
    {
      metric: "Equity",
      value: money(fsAttrs?.equity?.value, fsAttrs?.equity?.unit),
      details: "Total shareholders' equity",
    },
    {
      metric: "Equity Ratio",
      value: percent(fsAttrs?.equityRatio?.value ?? financial_summary?.equityRatio),
      details: "Proportion of equity to total assets",
    },
    {
      metric: "Liabilities",
      value: money(fsAttrs?.liabilities?.value, fsAttrs?.liabilities?.unit),
      details: "Total debt/obligations",
    },
    {
      metric: "Cash",
      value: money(fsAttrs?.cash?.value, fsAttrs?.cash?.unit),
      details: "Liquid assets",
    },
    {
      metric: "Capital",
      value: money(fsAttrs?.capital?.value, fsAttrs?.capital?.unit),
      details: "Registered capital",
    },
  ];
  
  const assetBreakdownRows = [
    {
      metric: "Real Estate",
      value: money(fsAttrs?.realEstate?.value, fsAttrs?.realEstate?.unit),
      details: "No details available",
    },
    {
      metric: "Receivables",
      value: money(fsAttrs?.receivables?.value, fsAttrs?.receivables?.unit),
      details: "No details available",
    },
    {
      metric: "Pension Provisions",
      value: money(fsAttrs?.pensionProvisions?.value, fsAttrs?.pensionProvisions?.unit),
      details: "No details available",
    },
  ];
  

  return (
    <View style={styles.section} break>
      <Text style={styles.sectionSeparator}>
        {`${t("4.")} ${t("Financial Summary Report")}`}
      </Text>

      <Text style={styles.alphListItem}>{`4.1 ${t(
        "Income Statement Highlights"
      )}`}</Text>
      <MetricTable t={t} rows={incomeStatementRows} />

      <Text style={styles.alphListItem}>{`4.2 ${t(
        "Efficiency & Profitability"
      )}`}</Text>
      <MetricTable t={t} rows={efficiencyRows} />

      <Text style={styles.alphListItem}>{`4.3 ${t(
        "Balance Sheet & Stability"
      )}`}</Text>
      <MetricTable t={t} rows={balanceSheetRows} />

      <Text style={styles.alphListItem}>{`4.4 ${t("Asset Breakdown")}`}</Text>
      <MetricTable t={t} rows={assetBreakdownRows} />
    </View>
  );
};

export default withNamespaces()(FinancialSummaryReport);
